/**
 * The app's own sidebar, replacing the vendored `Sidebar`. 2026-08-05 revamp.
 *
 * ## 🔴 Why this exists rather than restyling the vendored one
 *
 * `design/components/chrome/Sidebar.jsx` is built from **fourteen inline `style={{}}`
 * objects**. An inline style beats every external rule short of `!important`, so the vendored
 * chrome cannot be re-skinned from `app.css` — and `web/src/design/` is under a SHA-256
 * manifest that CI verifies, so it cannot be edited here either. Restyling it would have
 * meant a wall of `!important` fighting inline properties one at a time.
 *
 * So the **chrome** becomes app-owned while the **data components** stay vendored. That split
 * is not a workaround, it is the right seam: `DataTable`, `Num`, `MetricTile`, `Sparkline`,
 * `StatePill` and the rest read tokens, so they inherited the new world for free the moment
 * `app.css` redeclared it. The sidebar, top bar and status rail needed *structural* change —
 * they were the three things the operator named as glitchy — and structure is exactly what a
 * token cannot reach.
 *
 * ⚠ **`NAV` is still imported from the vendored module**, deliberately. `routes.ts` derives
 * the route table from it and its docstring gives the reason: two lists drift, and the way
 * they drift is a sidebar entry that navigates nowhere. The nav *model* stays vendored; only
 * its rendering moved. The vendored `Sidebar` is now used by `DesignGallery` alone, which is
 * a divergence to reconcile on the next upstream push, not a dead file.
 *
 * ## What changed, and each of these was a defect rather than a preference
 *
 * - **The active row was a slightly lighter grey with a 2px accent bar.** At `#191919` on
 *   `#090909` that is a 5-value step: the answer to *"which screen am I on"* was almost
 *   invisible. Now it is an `--accent-subtle` fill with `--accent` text and icon, and the
 *   coloured left bar is gone because a coloured border over 1px on a list item is the
 *   costume version of emphasis.
 * - **There were no hover, focus or press states.** Not subtle ones — absent. That is the
 *   failure mode a design language can produce by *prohibition*: the old register forbade
 *   them, so a reviewer sweeping for things wrongly present found nothing wrong.
 * - **The engine status sat in the sidebar's foot as a second footer**, one of three stacked
 *   bars. It belongs in the annunciator rail with the rest of the machine's state.
 * - **The theme toggle was a bare moon glyph in the top bar**, beside a kill switch, with no
 *   label. It became a labelled switch row at the foot of the sidebar, and then the operator
 *   asked for it smaller and back in the top right, which is right: a full-width labelled row
 *   with its own switch gave a once-a-year preference the same weight as a screen. It is a 28px
 *   icon button in `AppTopBar` now, with its accessible name on the button rather than beside it.
 */

import type { JSX } from 'react';

import { Icon } from '../design/components/icon/Icon.jsx';
import { Wordmark } from '../design/components/chrome/Wordmark.jsx';
import { NAV_GROUPS, type NavItem } from './routes';

export function AppSidebar({
  active,
  onNavigate,
  collapsed,
  onCollapse,
  menuOpen = false,
  onCloseMenu,
}: {
  active: string;
  onNavigate: (id: string) => void;
  collapsed: boolean;
  onCollapse: () => void;
  /** Phone only: whether the off-canvas drawer is showing. Ignored above 720px. */
  menuOpen?: boolean;
  onCloseMenu?: () => void;
}): JSX.Element {
  return (
    <nav
      className="jmt-side"
      data-collapsed={collapsed ? '' : undefined}
      data-open={menuOpen ? '' : undefined}
      // ⚠ Hidden from the accessibility tree when the drawer is shut, or a screen reader walks
      // fourteen off-canvas destinations before reaching the screen's own content.
      aria-hidden={undefined}
      aria-label="Screens"
    >
      <div className="jmt-side__brand">
        {collapsed ? (
          <Wordmark variant="mark" title="JM-Trade" />
        ) : (
          <Wordmark variant="horizontal" title="JM-Trade" />
        )}
        {/* Phone only. The scrim closes the drawer too, but a scrim is a discovery rather than an
            affordance, and the control that opened it is now behind the drawer. */}
        {onCloseMenu ? (
          <button
            type="button"
            className="jmt-side__close"
            onClick={onCloseMenu}
            aria-label="Close the menu"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        ) : null}
      </div>

      <div className="jmt-side__scroll">
        {NAV_GROUPS.map((group, index) => (
          <div
            className="jmt-side__group"
            key={group.group ?? `group-${index}`}
            // 🔴 **The grouping is spacing and a hairline, not three more words.** `RUN`, `DECIDE`
            // and `CAPITAL` were rendered as uppercase headings: 96px of height and three blocks
            // of text above a list whose items are already one word each. The operator's verdict
            // was *"way too much text"*, and a heading that labels four self-describing rows is
            // the clearest case of it — nobody reads `CAPITAL` to find `Wallet`.
            //
            // ⚠ **The grouping itself is kept**, because it is real: run / decide / capital is the
            // order of the morning. It is carried by a rule and a larger gap, which is what a
            // separator is for, and the group name survives as `aria-label` so the structure is
            // still announced.
            aria-label={group.group ?? undefined}
            role={group.group ? 'group' : undefined}
          >
            {index > 0 ? <hr className="jmt-side__rule" /> : null}
            <ul className="jmt-side__list">
              {group.items.map((item) => (
                <li key={item.id}>
                  <NavRow
                    item={item}
                    active={item.id === active}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* 🔴 **At the foot, and it has moved twice.** It began as a full-width `Toggle the sidebar
          rail` button sitting above the content on every screen — a preview affordance that leaked
          into the work area. It then went to the sidebar's header, beside the wordmark, where the
          operator's verdict was that it did not look good: the brand row is an identity, and
          hanging a utility control off its right edge made it read as a window chrome button.

          The foot is where it belongs. It is the least consequential thing in the navigation, it is
          pressed once and then forgotten, and putting it below the nav means the nav's own list
          runs uninterrupted from the wordmark to the bottom. It stays discrete — no border, no
          fill, `--text-disabled` ink — and resolves only on hover or focus. */}
      <div className="jmt-side__foot">
        <button
          type="button"
          className="jmt-side__collapse"
          onClick={onCollapse}
          aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar to icons'}
          title={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar to icons'}
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="4" width="18" height="16" rx="2" />
            <path d={collapsed ? 'M15 4v16' : 'M9 4v16'} />
          </svg>
          {/* Icon only. The word `Collapse` sat under fourteen one-word destinations and was the
              longest string in the column, on the least consequential control in it. The
              accessible name and the tooltip carry it. */}
        </button>
      </div>
    </nav>
  );
}

/**
 * One nav row.
 *
 * `aria-current="page"` rather than only a class: the active row must be the active row to a
 * screen reader too, and `--accent-subtle` is invisible to one.
 *
 * `item.glyph` is a trailing marker the nav model carries — `wallet` has `link-2`, meaning the
 * screen leaves for an external surface. It is drawn at the row's tail in tertiary, so it
 * reads as a property of the destination rather than as a second action.
 */
function NavRow({
  item,
  active,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate: (id: string) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className="jmt-side__row"
      data-active={active ? '' : undefined}
      aria-current={active ? 'page' : undefined}
      onClick={() => onNavigate(item.id)}
      // The `g`-then-letter accelerator, stated where the operator will look for it. It was
      // already implemented in `AppShell` and documented nowhere on screen.
      // 🔴 **The accelerator lives here, on every width.** A `<kbd>` badge used to be rendered on
      // every row at `opacity: 0` — invisible, and still **17.2px of layout** plus its 12px gap,
      // measured. So fourteen rows reserved a phantom column for a hint nobody could read, and
      // that is most of what made the right edge look wrong. The shortcut still works and is
      // still discoverable, in the one place that costs no pixels.
      title={`${item.label}${item.key ? ` (g ${item.key})` : ''}`}
    >
      {/* 17px, one under the vendored default, because S14's 29px row leaves 29px of box for it
          and an 18px glyph in a 29px row reads as filling the row rather than labelling it. The
          trailing `glyph` stays at 13px — it is a property of the destination, not a peer. */}
      <Icon name={item.icon} size={17} />
      {collapsed ? null : (
        <>
          <span className="jmt-side__label">{item.label}</span>
          {item.glyph ? (
            <span className="jmt-side__glyph" title="Opens an external surface">
              <Icon name={item.glyph} size={13} />
            </span>
          ) : null}
        </>
      )}
    </button>
  );
}
